import { initBehaviors, destroyBehaviors } from "..";
import breakpoints from "../../constants/breakpoints";
import events from "../../constants/events";
import { ajaxUpdate } from "../../utilities/ajax";
import { handleize } from "../../utilities/convertCase";
import { dispatchEvent } from "../../utilities/customEvents";
import { debounce } from "../../utilities/functions";
import {
  generateImageUrl,
  generateImageSrcset,
} from "../../utilities/imageUrls";

import { Base } from "./Base";

import cloneDeep from "lodash.clonedeep";
import template from "lodash.template";
import he from "he";
import animateScrollTo from "animated-scroll-to";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

const SESSION_STORAGE_KEY = "filterRootUrl";

const classes = {
  active: "active",
  checkbox: "product-filter__checkbox",
  disabled: "disabled",
  displayNone: "display-none",
  filterInitializing: "filter-initializing",
  grid: "ss-grid",
  jsHidden: "js-hidden",
  open: "open",
  paginationNext: "pagination__next",
  paginationNumber: "pagination__number",
  paginationPrevious: "pagination__previous",
  paginationPostDots: "pagination__dots--post",
  paginationPreDots: "pagination__dots--pre",
  productCardJson: "product-card__json",
  productCardTitle: "product-card__title",
  radio: "product-filter__radio",
  radioLabel: "product-detail__featured-option",
  selectedPill: "link.pill.active",
};

export class ProductFilter extends Base {
  constructor(behaviorName, element) {
    super(behaviorName, element);

    this.urlParams = [];
    this.wideExcludeIndexes = [];
    this.colorLookup = {};
    this.searchTerm = "";
    this.selectedRadios = [];
    this.selectedCheckboxes = [];
    this.appliedColors = [];
    this.appliedLengths = [];
    this.appliedSizes = [];
    this.appliedRadios = [];
    this.appliedTags = [];
    this.productsPerPage = 25;
    this.currentPage = 0;
    this.pages = [];
    this.radios = [];
    this.checkboxes = [];
    this.radioLabels = [];
    this.hideC2s = false;
    this.ctasPerPage = 2;
    this.filterCTAS = null;
    this.c2s = false;

    this.init();
  }

  init() {
    this.setFilterInitializing();
    this.initUrlParams();
    this.cacheSwatchData();
    this.cacheWideExcludeIndexes();
    this.buildColorLookup();
    this.cacheRadiosAndRadioLabels();
    this.cacheCheckboxes();
    this.initPageStatus();
    this.initSearchTermStatus();
    this.initRadioStatus();
    this.initCheckboxStatus();
    this.updateFilterToggleText();
    this.setClearSelectionDisabledStatus();
    this.bindRadioLabelListener();
    this.bindRadioListener();
    this.bindCheckboxListener();
    this.bindClearSelectionListener();
    this.bindSortByListener();
    this.bindFilterToggleListener();
    this.bindFilterCloseListener();
    this.bindWindowResizeHandler();
    this.startFilter();
    this.setFilterRootUrl();
  }

  setFilterRootUrl() {
    const { filterControls } = this.refs;
    if (
      filterControls &&
      filterControls.dataset.isFilterRoot &&
      filterControls.dataset.isFilterRoot == "true"
    ) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, window.location.href);
    }
    const rootUrl = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (rootUrl) {
      let selectedPills = Array.from(
        this.element.querySelectorAll("." + classes.selectedPill)
      );
      for (var i = 0; i < selectedPills.length; i++) {
        let item = selectedPills[i];
        item.href = rootUrl;
      }
    }
  }

  getFilterCTAS() {
    let totalPages = 0;

    if (this.filteredProducts) {
      totalPages = Math.ceil(
        this.filteredProducts.length / this.productsPerPage
      );
    }

    const filterCTASContainer = document.getElementById("filter-ctas");

    if (
      filterCTASContainer &&
      filterCTASContainer.children &&
      filterCTASContainer.children.length >= 1
    ) {
      const collectionId = this.element.dataset.collectionId;

      this.filterCTAS = [];
      if (collectionId) {
        for (let i = 0; i < filterCTASContainer.children.length; i++) {
          if (
            filterCTASContainer.children[i].dataset.collections.includes(
              collectionId
            )
          ) {
            this.filterCTAS.push(filterCTASContainer.children[i]);
          }
        }
      }

      if (this.filterCTAS.length <= 0) {
        this.filterCTAS = null;
        return;
      }

      // Setting up and array to have at least one CTA per page
      if (totalPages > this.filterCTAS.length) {
        for (let i = 0; i < totalPages; i++) {
          this.filterCTAS = this.filterCTAS.concat(this.filterCTAS);
          if (this.filterCTAS.length >= totalPages) {
            break;
          }
        }
      }
      // Duplicating amount of CTAS so we can take two per page
      this.filterCTAS = this.filterCTAS.concat(this.filterCTAS);
    } else {
      //console.log("RE / NO filterCTAS found ");
    }
  }

  setFilterInitializing() {
    this.element.classList.add(classes.filterInitializing);
    const { filterControls } = this.refs;
    if (filterControls.classList.contains("hide-c2s")) {
      this.hideC2s = true;
    }
    if (filterControls.classList.contains("c2s")) {
      this.c2s = true;
    }
  }

  unsetFilterInitializing() {
    this.element.classList.remove(classes.filterInitializing);
  }

  initUrlParams() {
    if (window.location.search.indexOf("?") > -1) {
      const params = window.location.search.split("?")[1].split("&");

      params.forEach((params) => {
        const paramArray = params.split("=");

        // Decode URI of queryString parameter value
        // This converts any occurances of escaped values into their normal counterparts
        // For instance %22 into "
        // We always try and match on the actual string as they can be encoded in many ways (" could be %22 or &quot; or &#34;)
        if (paramArray.length > 1) {
          paramArray[1] = decodeURI(paramArray[1]);
        }

        this.urlParams.push(paramArray);
      });

      this.urlParams = this.urlParams.reverse();
    }
  }

  cacheSwatchData() {
    const swatchDataElements = Array.from(
      document.querySelectorAll(".swatch-data")
    );

    if (swatchDataElements) {
      try {
        this.swatchData = swatchDataElements.reduce(
          (acc, cur) => [...JSON.parse(cur.innerHTML), ...acc],
          []
        );
      } catch (e) {
        // Error in the JSON
      }
    }
  }

  cacheWideExcludeIndexes() {
    let currentIndex = -1;
    let nextJump = 4;

    while (currentIndex + nextJump < this.productsPerPage) {
      currentIndex = currentIndex + nextJump;

      if (nextJump === 4) {
        nextJump = 3;
      } else {
        nextJump = 4;
      }

      this.wideExcludeIndexes.push(currentIndex);
    }
  }

  buildColorLookup() {
    this.swatchData.forEach((swatchItem) => {
      const handleizedFilterGroup = handleize(swatchItem.filter_group);

      if (!this.colorLookup[handleizedFilterGroup]) {
        this.colorLookup[handleizedFilterGroup] = [swatchItem.color_name];
      } else {
        this.colorLookup[handleizedFilterGroup].push(swatchItem.color_name);
      }
    });
  }

  cacheRadiosAndRadioLabels() {
    this.radios = Array.from(
      this.element.querySelectorAll("." + classes.radio)
    );
    this.radioLabels = Array.from(
      this.element.querySelectorAll("." + classes.radioLabel)
    );
  }

  cacheCheckboxes() {
    this.checkboxes = Array.from(
      this.element.querySelectorAll("." + classes.checkbox)
    );
  }

  initPageStatus() {
    for (let i = 0; i < this.urlParams.length; i++) {
      const param = this.urlParams[i];

      if (param[0] === "p") {
        this.currentPage = parseInt(param[1], 10) - 1;
        break;
      }
    }
  }

  initSearchTermStatus() {
    for (let i = 0; i < this.urlParams.length; i++) {
      const param = this.urlParams[i];

      if (param[0] === "q") {
        this.searchTerm = param[1];
        break;
      }
    }
  }

  updateUrlParam(name, value) {
    let paramExists = false;

    for (let i = 0; i < this.urlParams.length; i++) {
      const param = this.urlParams[i];

      if (param[0] === name) {
        paramExists = true;
        param[1] = value;
        break;
      }
    }

    if (!paramExists) {
      this.urlParams.push([name, value]);
    }
  }

  removeUrlParam(name) {
    let index = -1;

    for (let i = 0; i < this.urlParams.length; i++) {
      const param = this.urlParams[i];

      if (param[0] === name) {
        index = i;
        break;
      }
    }

    if (index > -1) {
      this.urlParams.splice(index, 1);
    }
  }

  initRadioStatus() {
    let radioSelected = false;
    this.urlParams.forEach((param) => {
      this.radios.forEach((radio) => {
        if (radio.name === param[0] && handleize(radio.value) === param[1]) {
          radio.checked = true;
          radioSelected = true;
          let existingRadioIndex = -1;

          for (let i = 0; i < this.selectedRadios.length; i++) {
            if (this.selectedRadios[i].name === param[0]) {
              existingRadioIndex = i;
              break;
            }
          }

          if (existingRadioIndex === -1) {
            this.selectedRadios.push(radio);
          } else {
            this.selectedRadios[existingRadioIndex] = radio;
          }
        }
      });
    });
  }

  updateSelectedRadios(radio) {
    let existingRadioIndex = -1;

    for (let i = 0; i < this.selectedRadios.length; i++) {
      if (this.selectedRadios[i].name === radio.name) {
        existingRadioIndex = i;
        break;
      }
    }

    if (existingRadioIndex === -1) {
      this.selectedRadios.push(radio);
    } else {
      this.selectedRadios[existingRadioIndex] = radio;
    }
  }

  removeRadio(radio) {
    let existingRadioIndex = -1;

    for (let i = 0; i < this.selectedRadios.length; i++) {
      if (this.selectedRadios[i].name === radio.name) {
        existingRadioIndex = i;
        break;
      }
    }

    if (existingRadioIndex > -1) {
      this.selectedRadios.splice(existingRadioIndex, 1);
    }
  }

  initCheckboxStatus() {
    this.urlParams.forEach((param) => {
      this.checkboxes.forEach((checkbox) => {
        if (
          checkbox.name === param[0] &&
          handleize(he.decode(checkbox.value)) === handleize(param[1])
        ) {
          checkbox.checked = true;
          this.selectedCheckboxes.push(checkbox);
        }
      });
    });
  }

  updateSelectedCheckboxes(checkbox) {
    const index = this.selectedCheckboxes.indexOf(checkbox);

    if (index > -1) {
      this.selectedCheckboxes.splice(index, 1);
    } else {
      this.selectedCheckboxes.unshift(checkbox);
    }

    let paramIndex = -1;

    this.urlParams.forEach((param, index) => {
      if (
        checkbox.name === param[0] &&
        handleize(he.decode(checkbox.value)) === handleize(param[1])
      ) {
        paramIndex = index;
      }
    });

    if (paramIndex === -1) {
      this.urlParams.push([
        checkbox.name,
        handleize(he.decode(checkbox.value)),
      ]);
    } else {
      this.urlParams.splice(paramIndex, 1);
    }
  }

  updateUrl() {
    const title = document.getElementsByTagName("title")[0].innerHTML;
    let url = this.buildUrl();

    if (!url) {
      url = window.location.toString();

      if (url.indexOf("?") > 0) {
        url = url.substring(0, url.indexOf("?"));
      }
    }

    if (history && history.replaceState) {
      history.replaceState({}, title, url);
    }
  }

  buildUrl() {
    let url = "";

    if (this.urlParams.length > 0) {
      url = "?";

      this.urlParams.forEach((param, index) => {
        url += `${param[0]}=${encodeURI(param[1])}`;
        if (index < this.urlParams.length - 1) {
          url += "&";
        }
      });
    }

    return url;
  }

  updateFilterToggleText() {
    const { filterToggleText } = this.refs;

    if (filterToggleText) {
      const appliedCount =
        this.selectedCheckboxes.length + this.selectedRadios.length;

      if (appliedCount > 0) {
        filterToggleText.innerHTML =
          appliedCount + " " + filterToggleText.dataset.textFiltersApplied;
      } else {
        filterToggleText.innerHTML = filterToggleText.dataset.textDefault;
      }
    }
  }

  bindRadioLabelListener() {
    this.radioLabels.forEach((radioLabel) => {
      radioLabel.addEventListener("click", (e) => {
        const radio = document.getElementById(radioLabel.getAttribute("for"));

        if (radio.checked) {
          e.preventDefault();
          radio.checked = false;
          this.removeRadio(radio);
          this.removeUrlParam(radio.name);
          this.updateFilter();
        }
      });
    });
  }

  bindRadioListener() {
    this.radios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        this.updateSelectedRadios(e.target);
        this.updateUrlParam(e.target.name, handleize(e.target.value));
        this.updateFilter();
      });
    });
  }

  bindCheckboxListener() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        this.updateSelectedCheckboxes(e.target);
        this.setClearSelectionDisabledStatus();
        this.updateFilter();
      });
    });
  }

  initFilteredProducts() {
    const { productIds } = this.refs;

    this.filteredProducts = [];

    if (productIds) {
      try {
        this.productIds = JSON.parse(productIds.innerHTML);
      } catch (e) {
        // Error in the JSON
      }

      if (this.productIds.length) {
        for (let i = 0; i < this.productIds.length; i++) {
          const product = window.mosmosh.productData[this.productIds[i]];
          if (product) {
            if (
              this.hideC2s &&
              product.tags &&
              product.tags.includes("c2s") > 0
            ) {
              // console.log("RE / SKIP THIS PRODUCT! ", product);
            } else {
              this.filteredProducts.push(cloneDeep(product));
            }
          }
        }
      }
    }
  }

  updateAppliedFilters() {
    this.appliedRadios = [];
    this.appliedColors = [];
    this.appliedLengths = [];
    this.appliedSizes = [];
    this.appliedTags = [];

    this.selectedRadios.forEach((radio) => {
      this.appliedRadios.push(radio.value);
    });

    this.selectedCheckboxes.forEach((checkbox) => {
      if (checkbox.name === "color") {
        this.appliedColors.push(he.decode(checkbox.value));
      }

      if (checkbox.name === "length") {
        this.appliedLengths.push(he.decode(checkbox.value));
      }

      if (checkbox.name === "size") {
        this.appliedSizes.push(he.decode(checkbox.value));
      }

      if (checkbox.name === "tag") {
        this.appliedTags.push(he.decode(checkbox.value));
      }
    });
  }

  filterProductsByColor() {
    if (this.appliedColors.length > 0) {
      this.filteredProducts = this.filteredProducts.filter((product) => {
        let productMatch = false;

        for (let i = 0; i < this.appliedColors.length; i++) {
          const descriptiveColors =
            this.colorLookup[handleize(this.appliedColors[i])] || [];

          if (descriptiveColors && descriptiveColors.length) {
            for (let j = 0; j < descriptiveColors.length; j++) {
              if (product.colors && product.colors.length) {
                for (let k = 0; k < product.colors.length; k++) {
                  if (
                    handleize(he.decode(product.colors[k])) ===
                    handleize(descriptiveColors[j])
                  ) {
                    product.color_match = product.colors[k];
                    productMatch = true;
                    break;
                  }
                }

                if (productMatch) {
                  break;
                }
              }
            }
          }

          if (productMatch) {
            break;
          }
        }

        if (productMatch) {
          return product;
        }
      });
    }
  }

  filterProductsByLength() {
    if (this.appliedLengths.length > 0) {
      this.filteredProducts = this.filteredProducts.filter((product) => {
        let productMatch = false;

        for (let i = 0; i < this.appliedLengths.length; i++) {
          if (product.lengths && product.lengths.length) {
            for (let j = 0; j < product.lengths.length; j++) {
              if (
                handleize(he.decode(product.lengths[j])) ===
                handleize(this.appliedLengths[i])
              ) {
                product.length_match = product.lengths[j];
                productMatch = true;
                break;
              }
            }

            if (productMatch) {
              break;
            }
          }
        }

        if (productMatch) {
          return product;
        }
      });
    }
  }

  filterProductsBySize() {
    if (this.appliedSizes.length > 0) {
      this.filteredProducts = this.filteredProducts.filter((product) => {
        let productMatch = false;
        for (let i = 0; i < this.appliedSizes.length; i++) {
          if (product.sizes && product.sizes.length) {
            for (let j = 0; j < product.sizes.length; j++) {
              if (
                handleize(he.decode(product.sizes[j])) ===
                handleize(this.appliedSizes[i])
              ) {
                productMatch = true;
                break;
              }
            }

            if (productMatch) {
              break;
            }
          }
        }

        if (productMatch) {
          return product;
        }
      });
    }
  }

  filterProductsByTag(filterArray) {
    if (filterArray.length > 0) {
      this.filteredProducts = this.filteredProducts.filter((product) => {
        let productMatch = false;

        for (let i = 0; i < filterArray.length; i++) {
          if (product.tags && product.tags.length) {
            for (let j = 0; j < product.tags.length; j++) {
              if (
                handleize(he.decode(product.tags[j])) ===
                handleize(filterArray[i])
              ) {
                productMatch = true;
                break;
              }
            }

            if (productMatch) {
              break;
            }
          }
        }

        if (productMatch) {
          return product;
        }
      });
    }
  }

  getCurrentVariant(product) {
    let currentVariantIndex = 0;
    let colorOptionIndex = -1;
    let lengthOptionIndex = -1;
    let foundMatch = false;

    if (product.color_match || this.searchTerm !== "") {
      for (let i = 0; i < product.options.length; i++) {
        if (product.options[i].name === "Color") {
          colorOptionIndex = i + 1;
          break;
        }
      }
    }

    if (product.length_match || this.searchTerm !== "") {
      for (let i = 0; i < product.options.length; i++) {
        if (product.options[i].name === "Length") {
          lengthOptionIndex = i + 1;
          break;
        }
      }
    }

    if (product.color_match || product.length_match) {
      for (let i = 0; i < product.variants.length; i++) {
        if (
          !product.color_match ||
          product.variants[i][`option${colorOptionIndex}`] ===
            product.color_match
        ) {
          if (
            !product.length_match ||
            product.variants[i][`option${lengthOptionIndex}`] ===
              product.length_match
          ) {
            currentVariantIndex = i;
            foundMatch = true;
            break;
          }
        }
      }

      // If we didn't find a match for both color and length, find the first color variant that matches
      if (!foundMatch) {
        for (let i = 0; i < product.variants.length; i++) {
          if (
            product.variants[i][`option${colorOptionIndex}`] ===
              product.color_match &&
            product.variants[i].available
          ) {
            currentVariantIndex = i;
            break;
          }
        }
      }
    } else if (this.searchTerm !== "") {
      const splitSearchTerm = this.searchTerm.split("+");

      if (lengthOptionIndex > -1) {
        let variantMatch = false;

        for (let i = 0; i < product.variants.length; i++) {
          for (let j = 0; j < splitSearchTerm.length; j++) {
            if (
              handleize(product.variants[i][`option${lengthOptionIndex}`]) ===
              handleize(splitSearchTerm[j])
            ) {
              product.length_match =
                product.variants[i][`option${lengthOptionIndex}`];
              currentVariantIndex = i;
              variantMatch = true;
              break;
            }
          }

          if (variantMatch) {
            break;
          }
        }
      }

      if (colorOptionIndex > -1) {
        let descriptiveColors = splitSearchTerm;

        for (let i = 0; i < splitSearchTerm.length; i++) {
          const associatedColors =
            this.colorLookup[handleize(splitSearchTerm[i])];

          if (associatedColors) {
            descriptiveColors = descriptiveColors.concat(associatedColors);
          }
        }

        let variantMatch = false;

        if (descriptiveColors && descriptiveColors.length) {
          for (let i = 0; i < product.variants.length; i++) {
            for (let j = 0; j < descriptiveColors.length; j++) {
              if (
                handleize(
                  he.decode(product.variants[i][`option${colorOptionIndex}`])
                ) === handleize(descriptiveColors[j])
              ) {
                product.color_match =
                  product.variants[i][`option${colorOptionIndex}`];
                currentVariantIndex = i;
                variantMatch = true;
                break;
              }
            }

            if (variantMatch) {
              break;
            }
          }
        }
      }
    }

    const currVariant = product.variants[currentVariantIndex];
    const variant = currVariant; //currVariant.available ? currVariant : product.variants.find(v => v.available);
    return variant || product.variants[0];
  }

  renderProducts() {
    const { content, noResultsTemplate } = this.refs;

    let cta1Slot = 4;
    let cta1MobileSlot = 6;

    let cta2Slot = 18;
    let cta2MobileSlot = 18;

    if (!this.filteredProducts) {
      this.filteredProducts = this.productData;
    }

    if (content) {
      destroyBehaviors(content);
      this.gridCounter = 0;
      this.gridCounterMobile = 0;
      let contentHTML = "";

      if (this.filteredProducts.length > 0) {
        let pageCount = 1;
        contentHTML = '<div class="ss-grid grid_page_1" data-reveal>';

        this.filteredProducts.forEach((product, index) => {
          if (
            (this.gridCounter > 0 &&
              this.gridCounter % this.productsPerPage === 0) ||
            this.gridCounter > this.productsPerPage
          ) {
            pageCount += 1;
            contentHTML +=
              '</div><div class="ss-grid grid_page_' +
              pageCount +
              ' display-none" data-reveal>';
            this.gridCounter = 0;
            this.gridCounterMobile = 0;
          }

          if (
            handleize(product.vendor) == "mos-mosh-gallery" ||
            handleize(product.vendor) == "gallery"
          ) {
            cta2Slot = 15;
          }

          if (this.filterCTAS != null) {
            const ctaCount = (pageCount - 1) * this.ctasPerPage;
            const cta1 = this.filterCTAS[ctaCount];
            const cta2 = this.filterCTAS[ctaCount + 1];

            const cta1HTML = cta1.outerHTML;
            const position1 = cta1.dataset.position;

            const cta2HTML = cta2.outerHTML;
            const position2 = cta2.dataset.position;
            let insertAfter;

            // Rendering fist CTA on the page on its second row after 4 items
            if (this.gridCounter === cta1MobileSlot) {
              contentHTML +=
                "<div class='ss-grid__item--8-of-8 ss-grid__item--2-of-8-md filter-cta-wrapper mobile'>" +
                cta1HTML +
                "</div>";
            }
            insertAfter = cta1Slot;
            if (position1 === "right") {
              insertAfter = cta1Slot + 3;
            }
            if (this.gridCounter === insertAfter) {
              contentHTML +=
                "<div class='ss-grid__item--8-of-8 ss-grid__item--2-of-8-md filter-cta-wrapper desktop'>" +
                cta1HTML +
                "</div>";
            }
            //

            // Rendering second CTA on the page on its second row after 4 items
            if (this.gridCounter === cta2MobileSlot) {
              contentHTML +=
                "<div class='ss-grid__item--8-of-8 ss-grid__item--2-of-8-md filter-cta-wrapper mobile'>" +
                cta2HTML +
                "</div>";
            }

            insertAfter = cta2Slot;
            if (position2 === "right") {
              insertAfter = cta2Slot + 3;
            }
            if (this.gridCounter === insertAfter) {
              contentHTML +=
                "<div class='ss-grid__item--8-of-8 ss-grid__item--2-of-8-md filter-cta-wrapper desktop'>" +
                cta2HTML +
                "</div>";
            }

            //
          }

          const template = this.compileTemplate(product, index);
          contentHTML += template;
        });

        contentHTML += "</div>";
      } else {
        if (noResultsTemplate) {
          contentHTML = noResultsTemplate.innerHTML;
        }
      }

      content.innerHTML = contentHTML;

      dispatchEvent(window, events.REVEAL_ITEMS_ADDED);
    }
  }

  compileTemplate(product) {
    const {
      productCardTemplate,
      productCardMediaItemTemplate,
      swatchTemplate,
      swatchItemTemplate,
    } = this.refs;

    if (
      productCardTemplate &&
      productCardMediaItemTemplate &&
      swatchTemplate &&
      swatchItemTemplate
    ) {
      // Get templates
      const productCardMarkup = productCardTemplate.innerHTML.trim();
      const productCardMediaItemMarkup =
        productCardMediaItemTemplate.innerHTML.trim();
      const swatchMarkup = swatchTemplate.innerHTML.trim();
      const swatchItemMarkup = swatchItemTemplate.innerHTML.trim();
      const currentVariant = this.getCurrentVariant(product);
      const c2s = product.tags && product.tags.includes("c2s") ? true : false;

      const isOnSale = c2s
        ? false
        : parseInt(currentVariant.compare_at_price_in_hundreds) >
          parseInt(currentVariant.price_in_hundreds);

      // Get color option index
      let colorOptionIndex = -1;

      for (let i = 0; i < product.options.length; i++) {
        if (product.options[i].name === "Color") {
          colorOptionIndex = i + 1;
          break;
        }
      }

      // Setup grid general media options
      let gridClasses = "ss-grid__item--4-of-8 ss-grid__item--2-of-8-md";
      let productCardClasses = "";
      let mediaClass = "media--product-card";
      let mediaSizes = "(max-width: 1023px) 50vw, 25vw";
      let wideSet = false;
      let wideSetMobile = false;

      if (
        product.grid_size === "wide" &&
        (this.gridCounter === 0 ||
          !this.wideExcludeIndexes.includes(this.gridCounter))
      ) {
        if (this.gridCounterMobile % 2 === 0) {
          gridClasses = "ss-grid__item--4-of-8-md";
          productCardClasses = "product-card--wide";
          mediaClass = "media--product-card-wide";
          mediaSizes = "(max-width: 1023px) 100vw, 50vw";
          wideSetMobile = true;
        } else {
          gridClasses = "ss-grid__item--4-of-8 ss-grid__item--4-of-8-md";
          productCardClasses = "product-card--wide-md";
          mediaClass = "media--product-card-wide-md";
          mediaSizes = "(max-width: 1023px) 50vw, 50vw";
        }
        wideSet = true;
      }

      this.gridCounter = this.gridCounter + 1;
      this.gridCounterMobile = this.gridCounterMobile + 1;

      const mod7 = (this.gridCounter - 5) % 7;

      if (this.gridCounter === 5 || (this.gridCounter > 5 && mod7 === 0)) {
        if (this.gridCounter === 5 && this.filterCTAS != null) {
          // Remove second row gap to add custom Filter CTA
        } else if (this.gridCounter === 19 && this.filterCTAS != null) {
        } else {
          gridClasses += " ss-grid__item--offset-1-md";
        }
      }

      gridClasses += " grid_product_" + this.gridCounter;

      if (wideSet) {
        this.gridCounter = this.gridCounter + 1;
      }

      if (wideSetMobile) {
        this.gridCounterMobile = this.gridCounterMobile + 1;
      }

      let settings = {
        showPackshot: false,
      };

      if (
        handleize(product.vendor) === "mos-mosh-gallery" ||
        handleize(product.vendor) === "gallery"
      ) {
        productCardClasses += " theme--theme-man";
        // settings.showPackshot = true;
      }

      if (isOnSale) {
        productCardClasses += " sale";
      }
      // Setup swatch
      let swatch = "";
      let swatchItems = "";
      let swatchActiveIndex = 0;

      const currentVariantColor =
        currentVariant && colorOptionIndex
          ? currentVariant["option" + colorOptionIndex]
          : null;

      if (product && product.colors) {
        for (let i = 0; i < product.colors.length; i++) {
          let classes = "";

          if (product.colors[i] === currentVariantColor) {
            swatchActiveIndex = i;
            classes = "active";
          }

          if (swatchItemMarkup) {
            const swatchItemTemplate = template(swatchItemMarkup);

            swatchItems += swatchItemTemplate({
              classes: classes,
              swatch_color: product.colors[i],
            });
          }
        }
      }

      if (swatchItems.length > 0 && swatchMarkup) {
        const swatchTemplate = template(swatchMarkup);

        swatch = swatchTemplate({
          swatch_items: swatchItems,
        });
      }

      // Setup media items

      // console.log(product);
      // console.log(currentVariant);
      // console.log(currentVariantColor);

      let modelshotIndex;
      let packshotFrontIndex;

      if (product && product.medias) {
        for (const [index, media] of product.medias.entries()) {
          // console.log(index, media);
          const mediaAltType = media.alt.split("/")[0].trim().toLowerCase();
          const mediaAltColor = media.alt.split("/")[1]
            ? media.alt.split("/")[1].trim().toLowerCase()
            : "";
          const mediaAltAngle = media.alt.split("/")[2]
            ? media.alt.split("/")[2].trim().toLowerCase()
            : "";
          // console.log(mediaAltColor);
          if (
            !modelshotIndex &&
            mediaAltType === "modelshot" &&
            product.colors.length &&
            currentVariantColor &&
            mediaAltColor === currentVariantColor.toLowerCase()
            // mediaAltColor === product.colors[0].toLowerCase()
          ) {
            modelshotIndex = index;
          }
          if (
            !packshotFrontIndex &&
            mediaAltType === "packshot" &&
            mediaAltColor === currentVariantColor &&
            currentVariantColor.toLowerCase() &&
            mediaAltAngle === "front"
          ) {
            packshotFrontIndex = index;
          }
        }
      }

      let mediaItems = "";
      let mediaItemArray = [];

      let defaultImageAlt = "";
      let defaultImageSrcset = "";
      let defaultImageUrl = "";

      defaultImageAlt = currentVariant.image_alt;
      defaultImageSrcset = generateImageSrcset(currentVariant.image_url);
      defaultImageUrl = generateImageUrl(currentVariant.image_url);

      if (settings.showPackshot) {
        if (product && product.medias && product.medias[packshotFrontIndex]) {
          defaultImageAlt = product.medias[packshotFrontIndex].alt;
          defaultImageSrcset = generateImageSrcset(
            product.medias[packshotFrontIndex].url
          );
          defaultImageUrl = generateImageUrl(
            product.medias[packshotFrontIndex].url
          );
        }

        mediaItemArray.push({
          imageAlt: defaultImageAlt,
          imageSrcset: defaultImageSrcset,
          imageUrl: defaultImageUrl,
        });
      } else {
        if (!defaultImageUrl) {
          defaultImageAlt = product.image_alt;
          defaultImageSrcset = generateImageSrcset(product.image_url);
          defaultImageUrl = generateImageUrl(product.image_url);
        }

        if (product && product.colors && product.colors.length > 1) {
          for (let i = 0; i < product.colors.length; i++) {
            let colorImageAlt = "";
            let colorImageSrcset = "";
            let colorImageUrl = "";

            for (let j = 0; j < product.variants.length; j++) {
              const { image_alt, image_url } = product.variants[j];

              if (
                product.variants[j][`option${colorOptionIndex}`] ===
                product.colors[i]
              ) {
                colorImageAlt = image_alt;
                colorImageSrcset = generateImageSrcset(image_url);
                colorImageUrl = generateImageUrl(image_url);
                break;
              }
            }

            if (colorImageUrl === "") {
              //If non found
              colorImageAlt = defaultImageAlt;
              colorImageSrcset = defaultImageSrcset;
              colorImageUrl = defaultImageUrl;
            }

            mediaItemArray.push({
              imageAlt: colorImageAlt,
              imageSrcset: colorImageSrcset,
              imageUrl: colorImageUrl,
            });
          }
        } else {
          mediaItemArray = [
            {
              imageAlt: defaultImageAlt,
              imageSrcset: defaultImageSrcset,
              imageUrl: defaultImageUrl,
            },
          ];
        }
      }

      for (let i = 0; i < mediaItemArray.length; i++) {
        const mediaItemTemplate = template(productCardMediaItemMarkup);

        let objectPosition = "center center";
        let mediaItemClass = mediaClass;

        if (
          product.grid_image_alignment !== "middle" &&
          mediaItemArray[i].imageAlt &&
          mediaItemArray[i].imageAlt.toLowerCase().indexOf("packshot") === -1
        ) {
          mediaItemClass += " media--align-" + product.grid_image_alignment;

          if (product.grid_image_alignment === "top") {
            objectPosition = "center 25%";
          }

          if (product.grid_image_alignment === "bottom") {
            objectPosition = "center 75%";
          }
        }

        mediaItems += mediaItemTemplate({
          display_class:
            i === swatchActiveIndex || settings.showPackshot ? "" : " hidden",
          image_alt: mediaItemArray[i].imageAlt,
          image_sizes: mediaSizes,
          image_srcset: mediaItemArray[i].imageSrcset,
          image_url: mediaItemArray[i].imageUrl,
          video_url: null,
          video_mime_type: null,
          video_alt: null,
          video_preview_image: null,
          media_class: mediaItemClass,
          object_position: objectPosition,
        });
      }

      // Setup tags
      const c2sTag = product.tags.includes("c2s")
        ? `<p class="text-tag-3 product-card__tag">${productCardTemplate.dataset.textC2s}</p>`
        : "";

      let labels = "";
      if (product.labels) {
        labels = product.labels
          .map((label) => {
            if (label === "New" && product.tags.includes("c2s")) {
              return "";
            }
            return `<p class="text-tag-3 product-card__tag">${label}</p>`;
          })
          .join("");
      }

      const sustainableTag = "";

      let exclusivesTag =
        String(product.tags).toLowerCase().includes("exclusives") ||
        product.tags.includes(window.mosmosh.translations.product.exclusives)
          ? `<p class="text-tag-3 product-card__tag">${
              productCardTemplate.dataset.textExclusives || "Exclusives"
            }</p>`
          : "";

      let webOnlyTag = String(product.tags).toLowerCase().includes("web only")
        ? `<p class="text-tag-3 product-card__tag">Web only</p>`
        : "";

      let hcSaleTag = "";

      if (
        product.available &&
        product.compare_at_price_min > product.price_min &&
        !product.tags.includes("c2s")
      ) {
        let discount =
          ((product.compare_at_price_min - product.price_min) * 100) /
          product.compare_at_price_min;
        if (discount != undefined) {
          hcSaleTag =
            '<div class="hc-sale-tag"><span>' +
            discount.toFixed(0) +
            "%</span></div>";
        }
      }

      // Setup price
      let price = currentVariant.price;

      if (currentVariant.compare_at_price && !c2s) {
        price =
          `<span class="product-card__original-price">${currentVariant.compare_at_price}</span>` +
          price;
      }
      const newPrice = this.element.dataset.newPrice;

      if (newPrice && currentVariant.compare_at_price && c2s) {
        price =
          price +
          ` <span class="product-card__new-price">(` +
          newPrice +
          " " +
          currentVariant.compare_at_price +
          `)</span>`;
      }

      // Setup URL
      let currentVariantUrl = `/products/${product.handle}?variant=${currentVariant.id}`;

      if (this.element.dataset.collectionLinkPrefix) {
        currentVariantUrl =
          this.element.dataset.collectionLinkPrefix + currentVariantUrl;
      }

      //Find index of first 'modelshot' for product
      const hoverMediaItemTemplate = template(productCardMediaItemMarkup);

      let hoverImage = product.medias.find((image) => {
        const altSplit = image.alt?.toLowerCase().split("/");

        if (altSplit && altSplit.length > 2) {
          const altType = altSplit[0].trim();
          const altColor = altSplit[1].trim();
          const altPos = altSplit[2].trim();

          return (
            image.type === "image" &&
            altColor === currentVariantColor?.toLowerCase() &&
            altType === "packshot" &&
            altPos === "front"
          );
        }
        return false;
      });

      if (!hoverImage) {
        hoverImage = product.medias.find((image) => {
          const altSplit = image.alt?.toLowerCase().split("/");

          if (altSplit && altSplit.length > 2) {
            const altType = altSplit[0].trim();
            const altColor = altSplit[1].trim();
            const altPos = altSplit[2].trim();

            return (
              image.type === "image" &&
              altColor === currentVariantColor?.toLowerCase() &&
              altType === "modelshot" &&
              altPos === "front"
            );
          }
          return false;
        });
      }

      //Use first modelshot as hover on men / gallery only
      let hoverMediaItem = hoverImage
        ? hoverMediaItemTemplate({
            display_class: " hover",
            image_alt: hoverImage.alt,
            image_sizes: mediaSizes,
            image_srcset: generateImageSrcset(hoverImage.url),
            image_url: generateImageSrcset(hoverImage.url),
            video_url: null,
            video_mime_type: null,
            video_alt: null,
            video_preview_image: null,
            media_class: mediaClass,
            object_position: "center center",
          })
        : null;

      // Build product card template
      const compiledTemplate = template(productCardMarkup);
      return compiledTemplate({
        grid_classes: gridClasses,
        json: JSON.stringify(product),
        media_items: mediaItems,
        media_hover: hoverMediaItem,
        price: price,
        product_card_classes: productCardClasses,
        exclusives_tag: exclusivesTag,
        web_only_tag: webOnlyTag,
        c2s_tag: c2sTag,
        labels: labels,
        c2s: c2s,
        sustainable_tag: sustainableTag,
        swatch: swatch,
        vendor: product.vendor,
        title: product.title,
        url: window.location.origin + currentVariantUrl,
        hc_sale_tag: hcSaleTag,
      });
    }
  }

  setPageVariables() {
    const { content } = this.refs;

    if (content) {
      this.pages = content.querySelectorAll("." + classes.grid);
    }
  }

  initPagination() {
    const { pagination } = this.refs;

    if (pagination) {
      let paginationHTML = "";

      if (this.pages.length > 1) {
        paginationHTML = this.buildPagination();
      }

      pagination.innerHTML = paginationHTML;
    }
  }

  buildPagination() {
    const { content, paginationTemplate } = this.refs;

    if ((content, paginationTemplate)) {
      const paginationMarkup = paginationTemplate.innerHTML.trim();

      let numbers = "";

      for (let i = 0; i < this.pages.length; i++) {
        let displayNumber = i + 1;

        if (i === this.pages.length - 1) {
          numbers +=
            '<span class="link-1 pagination__dots pagination__dots--post display-none">…</span>';
        }

        numbers += `<a href="#" class="link-1 pagination__number">${displayNumber}</a>`;

        if (i === 0) {
          numbers +=
            '<span class="link-1 pagination__dots pagination__dots--pre display-none">…</span>';
        }
      }

      const compiledPagination = template(paginationMarkup);

      return compiledPagination({
        numbers: numbers,
      });
    }
  }

  updatePaginationState() {
    const { pagination } = this.refs;

    if (pagination) {
      const next = pagination.querySelector("." + classes.paginationNext);
      const numbers = pagination.querySelectorAll(
        "." + classes.paginationNumber
      );
      const previous = pagination.querySelector(
        "." + classes.paginationPrevious
      );
      const postDots = pagination.querySelector(
        "." + classes.paginationPostDots
      );
      const preDots = pagination.querySelector("." + classes.paginationPreDots);

      if ((next, numbers, previous, postDots, preDots)) {
        let showPreDots = false;
        let showPostDots = false;

        if (this.currentPage === 0) {
          previous.classList.add(classes.disabled);
        } else {
          previous.classList.remove(classes.disabled);
        }

        for (let i = 0; i < numbers.length; i++) {
          let showNumber = false;

          if (i === this.currentPage) {
            numbers[i].classList.add(classes.active);
          } else {
            numbers[i].classList.remove(classes.active);
          }

          if (i === 0 || i === numbers.length - 1 || i === this.currentPage) {
            showNumber = true;
          }

          if (
            i < this.currentPage &&
            i >= this.currentPage - 2 &&
            !showNumber
          ) {
            showNumber = true;
          }

          if (
            i > this.currentPage &&
            i <= this.currentPage + 2 &&
            !showNumber
          ) {
            showNumber = true;
          }

          if (!showNumber) {
            numbers[i].classList.add(classes.displayNone);
          } else {
            numbers[i].classList.remove(classes.displayNone);
          }

          if (this.currentPage > 3) {
            showPreDots = true;
          }

          if (this.currentPage < numbers.length - 4) {
            showPostDots = true;
          }
        }

        if (showPreDots) {
          preDots.classList.remove(classes.displayNone);
        } else {
          preDots.classList.add(classes.displayNone);
        }

        if (showPostDots) {
          postDots.classList.remove(classes.displayNone);
        } else {
          postDots.classList.add(classes.displayNone);
        }

        if (this.currentPage === numbers.length - 1) {
          next.classList.add(classes.disabled);
        } else {
          next.classList.remove(classes.disabled);
        }
      }
    }
  }

  addPaginationListeners() {
    const { pagination } = this.refs;

    if (pagination) {
      const next = pagination.querySelector("." + classes.paginationNext);
      const numbers = pagination.querySelectorAll(
        "." + classes.paginationNumber
      );
      const previous = pagination.querySelector(
        "." + classes.paginationPrevious
      );

      if ((next, numbers, previous)) {
        next.addEventListener("click", (e) => {
          e.preventDefault();
          this.handlePaginationNextClick();
        });

        numbers.forEach((number, index) => {
          number.addEventListener("click", (e) => {
            e.preventDefault();
            this.handlePaginationNumberClick(index);
          });
        });

        previous.addEventListener("click", (e) => {
          e.preventDefault();
          this.handlePaginationPreviousClick();
        });
      }
    }
  }

  handlePaginationUpdate() {
    this.updatePaginationState();
    this.updateUrlParam("p", this.currentPage + 1);
    this.updateUrl();
    this.scrollThenShowActivePage();
  }

  handlePaginationPreviousClick() {
    if (this.currentPage !== 0) {
      this.currentPage = this.currentPage - 1;
      this.handlePaginationUpdate();
    }
  }

  handlePaginationNextClick() {
    if (this.currentPage !== this.pages.length - 1) {
      this.currentPage = this.currentPage + 1;
      this.handlePaginationUpdate();
    }
  }

  handlePaginationNumberClick(index) {
    this.currentPage = index;
    this.handlePaginationUpdate();
  }

  scrollThenShowActivePage() {
    let top = 0;
    if (this.c2s) {
      const c2sFilter = document.getElementById("c2s-filter-scrollto");
      if (c2sFilter) {
        top = c2sFilter.getBoundingClientRect().top + window.scrollY - 100;
      }
    }

    animateScrollTo(top, {
      maxDuration: 500,
    }).then(() => {
      this.showActivePage();
    });
  }

  showActivePage() {
    this.pages.forEach((page, index) => {
      if (index === this.currentPage) {
        page.classList.remove(classes.displayNone);
        initBehaviors(page);
      } else {
        if (!page.classList.contains(classes.displayNone)) {
          page.classList.add(classes.displayNone);
          destroyBehaviors(page);
        }
      }
    });

    dispatchEvent(window, events.FILTER_CONTENT_UPDATED);
  }

  setReady() {
    const { content, pagination } = this.refs;

    if (content && pagination) {
      content.classList.remove(classes.jsHidden);
      pagination.classList.remove(classes.jsHidden);
      dispatchEvent(window, events.FILTER_CONTENT_UPDATED);
    }
  }

  bindSortByListener() {
    const { sortby, sortbyContainer } = this.refs;

    if (sortby && sortbyContainer) {
      sortby.addEventListener("change", (e) => {
        this.setSortByDisabled();
        this.currentPage = 0;
        this.updateUrlParam("sort_by", e.target.value);
        this.updateUrlParam("p", this.currentPage + 1);
        this.updateUrl();
        this.updateProductDataJson(() => {
          this.setSortByEnabled();
          this.performFiltering();
        });
      });
    }
  }

  setSortByDisabled() {
    const { sortby, sortbyContainer } = this.refs;

    if (sortby && sortbyContainer) {
      sortbyContainer.classList.add(classes.disabled);
      sortby.setAttribute("disabled", "disabled");
    }
  }

  setSortByEnabled() {
    const { sortby, sortbyContainer } = this.refs;

    if (sortby && sortbyContainer) {
      sortbyContainer.classList.remove(classes.disabled);
      sortby.removeAttribute("disabled");
    }
  }

  updateProductDataJson(callback) {
    ajaxUpdate(window.location.toString(), {
      callback: callback,
      history: false,
      selectors: ['[data-ref-product-filter="product-ids"]'],
    });
  }

  bindClearSelectionListener() {
    const { clearSelection } = this.refs;

    if (clearSelection) {
      clearSelection.addEventListener("click", (e) => {
        e.preventDefault();
        this.resetFilter();
      });
    }
  }

  setClearSelectionDisabledStatus() {
    const { clearSelection } = this.refs;

    if (clearSelection) {
      if (this.selectedCheckboxes.length > 0) {
        clearSelection.removeAttribute("disabled");
      } else {
        clearSelection.setAttribute("disabled", "disabled");
      }
    }
  }

  resetFilter() {
    this.currentPage = 0;

    let resetUrlParams = [];

    this.urlParams.forEach((param) => {
      if (param[0] === "type" || param[0] === "q") {
        resetUrlParams.push(param);
      }
    });

    this.urlParams = resetUrlParams;

    this.selectedCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    this.selectedCheckboxes = [];

    this.setClearSelectionDisabledStatus();
    this.updateUrl();
    this.updateFilterToggleText();
    this.performFiltering();
  }

  bindFilterToggleListener() {
    const { filterToggle } = this.refs;

    if (filterToggle) {
      filterToggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleFilterControls();
      });
    }
  }

  bindFilterCloseListener() {
    const { filterClose } = this.refs;

    if (filterClose) {
      filterClose.addEventListener("click", (e) => {
        e.preventDefault();
        this.closeFilterControls();
      });
    }
  }

  openFilterControls() {
    const { filterControls } = this.refs;

    if (filterControls) {
      if (window.innerWidth < breakpoints.MD) {
        animateScrollTo(0, {
          maxDuration: 500,
        }).then(() => {
          filterControls.classList.add(classes.open);
          this.setSortByDisabled();
          disableBodyScroll(filterControls);
        });
      } else {
        filterControls.classList.add(classes.open);
        this.setSortByDisabled();
      }
    }
  }

  closeFilterControls() {
    const { filterControls } = this.refs;

    if (filterControls) {
      filterControls.classList.remove(classes.open);
      this.setSortByEnabled();

      if (window.innerWidth < breakpoints.MD) {
        enableBodyScroll(filterControls);
      }
    }
  }

  toggleFilterControls() {
    const { filterControls } = this.refs;

    if (filterControls) {
      if (filterControls.classList.contains(classes.open)) {
        this.closeFilterControls();
      } else {
        this.openFilterControls();
      }
    }
  }

  bindWindowResizeHandler() {
    this.resizeHandler = debounce(() => {
      this.handleResize();
    }, 250);

    window.addEventListener("resize", this.resizeHandler);
  }

  handleResize() {
    const { filterControls } = this.refs;

    if (filterControls) {
      if (
        filterControls.classList.contains(classes.open) &&
        window.innerWidth < breakpoints.MD
      ) {
        disableBodyScroll(filterControls);
      } else {
        enableBodyScroll(filterControls);
      }
    }
  }

  startFilter() {
    if (window.mosmosh.productDataLoaded) {
      this.unsetFilterInitializing();
      this.performFiltering();
    } else {
      this.productDataLoadedHandler = () => {
        this.unsetFilterInitializing();
        this.performFiltering();
      };

      window.addEventListener(
        events.PRODUCT_DATA_LOADED,
        this.productDataLoadedHandler
      );
    }
  }

  performFiltering() {
    this.initFilteredProducts();
    this.updateAppliedFilters();
    this.filterProductsByColor();
    this.filterProductsByLength();
    this.filterProductsBySize();
    this.filterProductsByTag(this.appliedRadios);
    this.filterProductsByTag(this.appliedTags);
    this.getFilterCTAS();
    this.renderProducts();
    this.setPageVariables();
    this.initPagination();
    this.updatePaginationState();
    this.addPaginationListeners();
    this.showActivePage();
    this.setReady();
  }

  updateFilter() {
    this.currentPage = 0;
    this.updateUrlParam("p", this.currentPage + 1);
    this.updateUrl();
    this.updateFilterToggleText();
    this.performFiltering();
  }

  cleanup() {
    window.removeEventListener("resize", this.resizeHandler);
    window.removeEventListener(
      events.PRODUCT_DATA_LOADED,
      this.productDataLoadedHandler
    );
  }
}
